import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi, ApiError } from '../lib/api';
import Wordmark from '../components/Wordmark';
import Eyebrow from '../components/Eyebrow';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/Button';

interface Props {
  mode: 'setup' | 'login';
}

export default function AdminAuth({ mode }: Props) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApi
      .status()
      .then((s) => {
        if (cancelled) return;
        if (mode === 'setup' && !s.needsSetup) navigate('/admin/login', { replace: true });
        if (mode === 'login' && s.needsSetup) navigate('/admin/setup', { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [mode, navigate]);

  const isSetup = mode === 'setup';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (isSetup && password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (isSetup && password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      if (isSetup) {
        await authApi.setup(password, setupSecret);
      } else {
        await authApi.login(password, needsTotp ? code : undefined);
      }
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Server may flag that TOTP is required (e.g. it was just turned
        // on in another tab, or the status race-conditioned). The 401
        // message contains "TOTP" — surface the field so the user can
        // retry without losing the password they already typed.
        if (err.status === 401 && /TOTP/i.test(err.message)) {
          setNeedsTotp(true);
        }
        setError(err.message);
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-page text-fg flex flex-col">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" aria-label="Back to home">
          <Wordmark size="md" />
        </Link>
        <ThemeToggle />
      </header>

      <section className="flex-1 flex items-center justify-center px-6 py-8">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md bg-card rounded-md border border-line p-8 md:p-10 animate-scale-in"
        >
          <Eyebrow accent>// {isSetup ? 'FIRST RUN' : 'ADMIN'}</Eyebrow>
          <h1 className="font-display font-bold text-[32px] leading-[1.1] tracking-[-0.02em] mt-3 mb-2">
            {isSetup ? 'Set up your admin' : 'Sign in'}
          </h1>
          <p className="text-muted text-[15px] leading-[1.55] mb-8">
            {isSetup
              ? 'Choose a password. Used only by you to manage scripts.'
              : 'Enter your admin password to continue.'}
          </p>

          {isSetup && (
            <Field
              label="Setup secret"
              type="password"
              value={setupSecret}
              onChange={setSetupSecret}
              autoFocus
              disabled={busy}
              autoComplete="one-time-code"
            />
          )}

          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoFocus={!isSetup}
            disabled={busy}
            className={isSetup ? 'mt-4' : ''}
          />

          {isSetup && (
            <Field
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={setConfirm}
              disabled={busy}
              className="mt-4"
            />
          )}

          {!isSetup && needsTotp && (
            <Field
              label="Two-factor code"
              type="text"
              value={code}
              onChange={(v) => setCode(v.replace(/\s/g, ''))}
              disabled={busy}
              className="mt-4"
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="6-digit code"
            />
          )}

          {error && (
            <p className="text-[13px] text-accent mt-4" role="alert">
              {error}
            </p>
          )}

          <div className="mt-8">
            <Button
              type="submit"
              variant="primary"
              glow
              disabled={busy || !password || (isSetup && (!setupSecret || !confirm))}
              className="w-full justify-center"
            >
              {busy ? 'One moment…' : isSetup ? 'Create admin' : 'Sign in'}
            </Button>
          </div>
        </form>
      </section>

      <footer className="px-6 md:px-12 py-6">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint">
          Sheahan Studios · Confidential
        </span>
      </footer>
    </main>
  );
}

interface FieldProps {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | 'url' | 'search';
  placeholder?: string;
}

function Field({
  label,
  type,
  value,
  onChange,
  autoFocus,
  disabled,
  className = '',
  autoComplete,
  inputMode,
  placeholder,
}: FieldProps) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        disabled={disabled}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        className="
          w-full px-4 py-3
          bg-elevated border border-line rounded-sm
          text-fg text-[15px]
          transition-all duration-150
          focus:border-accent focus:shadow-focus focus:outline-none
          disabled:opacity-50
        "
      />
    </label>
  );
}
